import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Mail, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api, setToken, getToken } from "@/lib/api";
import { ADMIN } from "@/constants/testIds";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    // if already logged in, verify token and redirect
    if (getToken()) {
      api.get("/admin/me").then(() => nav("/admin/dashboard")).catch(() => {});
    }
  }, [nav]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post("/admin/login", { email, password });
      setToken(res.data.token);
      toast.success(`Selamat datang, ${res.data.user?.name || res.data.user?.email}`);
      nav("/admin/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login gagal");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6 relative overflow-hidden">
      {/* Decorative */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#DC2626]/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#DC2626]/5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md relative z-10"
      >
        <div className="flex items-center gap-2.5 mb-8 justify-center">
          <div>
            <div className="font-display font-bold text-lg leading-none tracking-tight text-center">
              Admin Dashboard
            </div>
            <div className="text-[11px] text-slate-500 leading-none mt-1.5 text-center">
              Chatbot Widget
            </div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-[0_4px_24px_rgba(0,0,0,0.04)]">
          <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900">
            Masuk ke Dashboard
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Kelola leads dan percakapan chatbot
          </p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <label className="text-xs font-medium text-slate-700 mb-1.5 block">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  data-testid={ADMIN.loginEmail}
                  type="email"
                  className="w-full pl-10 pr-3 py-2.5 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  placeholder="admin@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700 mb-1.5 block">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  data-testid={ADMIN.loginPassword}
                  type="password"
                  className="w-full pl-10 pr-3 py-2.5 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
            </div>

            <button
              data-testid={ADMIN.loginSubmit}
              type="submit"
              disabled={loading}
              className="w-full bg-[#DC2626] hover:bg-[#B91C1C] text-white font-medium text-sm py-2.5 px-4 rounded-lg flex items-center justify-center gap-2 disabled:opacity-60 transition-colors"
            >
              {loading ? "Memproses..." : (
                <>
                  Masuk
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </form>
        </div>

        <div className="text-center mt-6">
          <a href="/" className="text-xs text-slate-500 hover:text-slate-900 transition-colors">
            ← Kembali ke halaman utama
          </a>
        </div>
      </motion.div>
    </div>
  );
}
